import React, { useState, useEffect } from "react"
import moment from "moment"
import * as XLSX from "xlsx"

import { styled } from "@mui/material/styles"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import Grid from "@mui/material/Grid"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell, { tableCellClasses } from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import RefreshIcon from "@mui/icons-material/Refresh"
import IconButton from "@mui/material/IconButton"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Alert from "@mui/material/Alert"

import AppApi from "../utils/AppApi"

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}))

const formatDuration = (minutes) => {
  if (minutes < 1) {
    return "< 1 นาที"
  }
  if (minutes < 60) {
    return `${Math.round(minutes)} นาที`
  }
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return `${hours} ชม. ${mins} นาที`
}

const getDurationColor = (minutes, stillInside = false) => {
  if (minutes >= 35) {
    if (stillInside)
      return {
        color: "default",
        sx: { backgroundColor: "#81c784", color: "white" },
      }
    return { color: "success", sx: {} }
  }
  if (stillInside) return { color: "warning", sx: {} }
  return { color: "error", sx: {} }
}

const TruckChaAm = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [openExportDialog, setOpenExportDialog] = useState(false)
  const [exportStartDate, setExportStartDate] = useState(
    moment().subtract(1, "day").startOf("day").format("YYYY-MM-DD")
  )
  const [exportStartTime, setExportStartTime] = useState("00:00")
  const [exportEndDate, setExportEndDate] = useState(moment().format("YYYY-MM-DD"))
  const [exportEndTime, setExportEndTime] = useState("23:59")
  const [exportLoading, setExportLoading] = useState(false)
  const [exportError, setExportError] = useState(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await AppApi.get("/truck_cha_am_history")
      setData(response.data)
      setLastUpdate(new Date())
    } catch (err) {
      console.error("Error fetching Cha Am data:", err)
      setError("ไม่สามารถโหลดข้อมูลได้")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 60000) // Refresh every 1 minute
    return () => clearInterval(interval)
  }, [])

  const handleExportClick = () => {
    setOpenExportDialog(true)
  }

  const handleExportClose = () => {
    setOpenExportDialog(false)
    setExportError(null)
  }

  const handleExportSubmit = async () => {
    setExportLoading(true)
    setExportError(null)
    try {
      // Create datetime strings with timezone
      const startDateTime = moment(
        `${exportStartDate}T${exportStartTime}`
      ).format()
      const endDateTime = moment(
        `${exportEndDate}T${exportEndTime}:59`
      ).format()

      // Call API with date parameters
      const response = await AppApi.get("/truck_cha_am_history", {
        params: {
          start_date: startDateTime,
          end_date: endDateTime,
        },
      })

      // Prepare data for Excel export
      const excelData = []
      if (response.data.trucks) {
        response.data.trucks.forEach((truck) => {
          truck.visits.forEach((visit, visitIndex) => {
            excelData.push({
              "ทะเบียนรถ": truck.truck_no,
              "รอบที่": truck.visit_count > 1 ? `${visitIndex + 1}/${truck.visit_count}` : "-",
              "เวลาเข้า": moment(visit.enter_time).format("DD/MM/YYYY HH:mm:ss"),
              "เวลาออก": visit.still_inside ? "-" : moment(visit.exit_time).format("DD/MM/YYYY HH:mm:ss"),
              "ระยะเวลาจอด (นาที)": visit.still_inside
                ? moment().diff(moment(visit.enter_time), "minutes")
                : visit.duration_minutes,
              "สถานะ": visit.still_inside ? "อยู่ในพื้นที่" : "ออกแล้ว",
            })
          })
        })
      }

      // Create workbook
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(excelData)
      XLSX.utils.book_append_sheet(workbook, worksheet, "Truck Cha-Am")

      // Set column widths
      const columnWidths = [
        { wch: 15 },
        { wch: 12 },
        { wch: 20 },
        { wch: 20 },
        { wch: 18 },
        { wch: 15 },
      ]
      worksheet["!cols"] = columnWidths

      // Generate filename with date range
      const filename = `truck-cha-am-${exportStartDate}_to_${exportEndDate}.xlsx`
      XLSX.writeFile(workbook, filename)

      handleExportClose()
    } catch (err) {
      console.error("Error exporting data:", err)
      setExportError("ไม่สามารถส่งออกข้อมูลได้")
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LocalShippingIcon sx={{ fontSize: 40, color: "primary.main" }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                รถที่จอดพักที่ชะอำ
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cha-Am Rest Stop Truck History
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {lastUpdate && (
              <Typography variant="body2" color="text.secondary">
                อัพเดตล่าสุด: {moment(lastUpdate).format("HH:mm:ss")}
              </Typography>
            )}
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportClick}
              disabled={loading || !data}
              sx={{ fontWeight: "bold", px: 2 }}
            >
              Export Excel
            </Button>
            <IconButton onClick={fetchData} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {data && (
        <Grid container spacing={1} sx={{ mb: 3, mt: 1 }}>
          <Grid item xs={6} sm={3} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                textAlign: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
              >
                จำนวนรถทั้งหมด
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {data.truck_count} คัน
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                textAlign: "center",
                background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                color: "white",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
              >
                ช่วงเวลาข้อมูล
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {data.period.hours} ชม.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                textAlign: "center",
                background: "linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%)",
                color: "white",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
              >
                เริ่มต้น
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {moment(data.period.start).format("DD/MM HH:mm")}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                textAlign: "center",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
              >
                สิ้นสุด
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {moment(data.period.end).format("DD/MM HH:mm")}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {loading && !data && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Paper
          elevation={3}
          sx={{ p: 3, textAlign: "center", backgroundColor: "error.light" }}
        >
          <Typography color="white">{error}</Typography>
        </Paper>
      )}

      {data && data.trucks && (
        <TableContainer
          component={Paper}
          elevation={3}
          sx={{ borderRadius: 3 }}
        >
          <Table sx={{ minWidth: 700 }} aria-label="truck cha am table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="center" width={60}>
                  #
                </StyledTableCell>
                <StyledTableCell width={120}>ทะเบียนรถ</StyledTableCell>
                <StyledTableCell align="center" width={80}>
                  รอบที่
                </StyledTableCell>
                <StyledTableCell align="center">เวลาเข้า</StyledTableCell>
                <StyledTableCell align="center">เวลาออก</StyledTableCell>
                <StyledTableCell align="center" width={150}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <AccessTimeIcon fontSize="small" />
                    ระยะเวลาจอด
                  </Box>
                </StyledTableCell>
                <StyledTableCell align="center" width={100}>
                  สถานะ
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Flatten visits with truck info and sort by enter_time descending
                const allVisits = data.trucks.flatMap((truck) =>
                  truck.visits.map((visit, visitIndex) => ({
                    ...visit,
                    truck_no: truck.truck_no,
                    visit_count: truck.visit_count,
                    visit_number: visitIndex + 1,
                  }))
                )
                allVisits.sort(
                  (a, b) => new Date(b.enter_time) - new Date(a.enter_time)
                )

                return allVisits.map((visit, index) => (
                  <StyledTableRow
                    key={`${visit.truck_no}-${visit.visit_number}-${index}`}
                  >
                    <StyledTableCell align="center">
                      <Typography fontWeight="medium" color="text.secondary">
                        {index + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      <Chip
                        label={visit.truck_no}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: "bold" }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2">
                        {visit.visit_count > 1
                          ? `${visit.visit_number}/${visit.visit_count}`
                          : "-"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2">
                        {moment(visit.enter_time).format("DD/MM/YY HH:mm")}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2">
                        {visit.still_inside
                          ? "-"
                          : moment(visit.exit_time).format("DD/MM/YY HH:mm")}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {(() => {
                        const duration = visit.still_inside
                          ? moment().diff(moment(visit.enter_time), "minutes")
                          : visit.duration_minutes
                        const colorStyle = getDurationColor(
                          duration,
                          visit.still_inside
                        )
                        return (
                          <Chip
                            label={formatDuration(duration)}
                            color={colorStyle.color}
                            size="small"
                            sx={{
                              fontWeight: "bold",
                              minWidth: 100,
                              ...colorStyle.sx,
                            }}
                          />
                        )
                      })()}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {visit.still_inside ? (
                        <Chip
                          label="อยู่ในพื้นที่"
                          color="info"
                          size="small"
                          sx={{ fontWeight: "bold" }}
                        />
                      ) : (
                        <Typography variant="body2" fontWeight="medium">
                          ออกแล้ว
                        </Typography>
                      )}
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Export Dialog */}
      <Dialog open={openExportDialog} onClose={handleExportClose} maxWidth="sm" fullWidth>
        <DialogTitle>ส่งออกข้อมูล (Export to Excel)</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {exportError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {exportError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                วันที่เริ่มต้น
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="time"
                  value={exportStartTime}
                  onChange={(e) => setExportStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: 120 }}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                วันที่สิ้นสุด
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="time"
                  value={exportEndTime}
                  onChange={(e) => setExportEndTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: 120 }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExportClose} color="inherit">
            ยกเลิก
          </Button>
          <Button
            onClick={handleExportSubmit}
            variant="contained"
            color="primary"
            disabled={exportLoading}
          >
            {exportLoading ? "กำลังส่งออก..." : "ส่งออก"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TruckChaAm
